package ai.wagemix.logic.picker

import ai.wagemix.net.models.asTree
import ai.wagemix.net.models.name
import ai.wagemix.net.models.WMOccupationTaxonomy

/*
 * Pure functions of the occupation picker over the taxonomy already loaded: which nodes a search
 * keeps, how many leaf occupations sit under each group, which groups to expand to reveal the
 * selection, and the visible rows. The presenter calls them; nothing here holds state.
 */

/**
 * The search needle for [query], or `null` when the query is shorter than
 * [WMOccupationPickerPresenter.MIN_QUERY_LENGTH] and the whole tree shows.
 */
internal fun pickerNeedleOf(query: String): String? {
    val trimmed = query.trim()
    return if (trimmed.length >= WMOccupationPickerPresenter.MIN_QUERY_LENGTH) trimmed.lowercase() else null
}

/**
 * The ids a search for [needle] keeps (the root excluded): every node whose name contains it, every
 * descendant of such a node (a matching group keeps its whole subtree), and every ancestor of a kept
 * node (so the hierarchy shows). `null` means no filter.
 */
internal fun WMOccupationTaxonomy.Tree.keptIdsFor(needle: String?): Set<Int>? {
    if (needle == null) {
        return null
    }
    val kept = mutableSetOf<Int>()
    fun walk(node: WMOccupationTaxonomy.Tree, underMatch: Boolean): Boolean {
        val matches = underMatch || node.item.name.lowercase().contains(needle)
        var anyKept = matches
        node.children.forEach { child ->
            if (walk(child, matches)) {
                anyKept = true
            }
        }
        if (anyKept) {
            kept += node.item.id
        }
        return anyKept
    }
    children.forEach { walk(it, underMatch = false) }
    return kept
}

/**
 * The number of leaf occupations under each group of this tree (the root included). Leaves have
 * no entry. With [kept], only kept leaves are counted, so a group counts its matches.
 */
internal fun WMOccupationTaxonomy.Tree.leafCounts(kept: Set<Int>? = null): Map<Int, Int> {
    val counts = mutableMapOf<Int, Int>()
    fun walk(node: WMOccupationTaxonomy.Tree): Int {
        if (node.children.isEmpty()) {
            return if (kept == null || node.item.id in kept) 1 else 0
        }
        val count = node.children.sumOf { walk(it) }
        counts[node.item.id] = count
        return count
    }
    walk(this)
    return counts
}

/** The number of leaf occupations a search for [needle] keeps (a matching group keeps all of its own). */
internal fun WMOccupationTaxonomy.searchResultCount(needle: String): Int {
    val tree = asTree()
    return tree.leafCounts(tree.keptIdsFor(needle))[tree.item.id] ?: 0
}

/** The ids of the groups above [id], nearest first, without the root. */
internal fun WMOccupationTaxonomy.ancestorIdsOf(id: Int): List<Int> {
    val parentById = items.associate { it.id to it.parentId }
    val result = mutableListOf<Int>()
    var current = parentById[id]
    while (current != null && current != root.id) {
        result += current
        current = parentById[current]
    }
    return result
}

/**
 * Every node that covers [id]: the groups above it and the root ("All occupations"). A mix never holds a node together
 * with one that covers it, because the covering node's wages already include it.
 */
internal fun WMOccupationTaxonomy.coveringIdsOf(id: Int): Set<Int> {
    return (ancestorIdsOf(id) + root.id).filterNot { it == id }.toSet()
}

/**
 * [expanded] plus every group above a [selected] node that the search for [needle] still keeps
 * (every selected node when there is no filter), so the selection is visible.
 */
internal fun WMOccupationTaxonomy.expansionRevealing(
    selected: Set<Int>,
    needle: String?,
    expanded: Set<Int>,
): Set<Int> {
    val kept = asTree().keptIdsFor(needle)
    val stillMatching = selected.filter { kept == null || it in kept }
    return expanded + stillMatching.flatMap { ancestorIdsOf(it) }
}

/**
 * The visible rows: the tree, or the tree pruned to what the search for [needle] keeps, walked
 * through the [expanded] groups.
 */
internal fun WMOccupationTaxonomy.pickerRows(
    needle: String?,
    expanded: Set<Int>,
    selected: Set<Int>,
    inMix: Set<Int>,
): List<WMOccupationPickerRow> {
    val tree = asTree()
    val kept = tree.keptIdsFor(needle)
    val counts = tree.leafCounts(kept)
    val rows = mutableListOf<WMOccupationPickerRow>()
    fun walk(node: WMOccupationTaxonomy.Tree, depth: Int, parentSelected: Boolean) {
        node.children.filter { kept == null || it.item.id in kept }.forEach { child ->
            val id = child.item.id
            val visibleChildren = child.children.any { kept == null || it.item.id in kept }
            val isExpanded = visibleChildren && id in expanded
            rows += WMOccupationPickerRow(
                occupationId = id,
                name = child.item.name,
                depth = depth,
                descendantCount = counts[id] ?: 0,
                countsMatches = kept != null,
                hasChildren = visibleChildren,
                expanded = isExpanded,
                inMix = id in inMix,
                parentSelected = parentSelected,
                selected = id in selected,
            )
            if (isExpanded) {
                walk(child, depth + 1, parentSelected || id in selected)
            }
        }
    }
    walk(tree, depth = 0, parentSelected = false)
    return rows
}
